import {enableMapSet} from 'immer';
import {create} from 'zustand';
import {immer} from 'zustand/middleware/immer';
import {persist} from 'zustand/middleware';
import {Key} from 'react';
import {DriveEntry} from './drive-entry';
import { RoutesPage } from '@app/routes';


const stableArray: DriveEntry[] = [];
enableMapSet();

export type DriveDialog = 'rename' | 'mint' | 'share' | 'getLink';

interface DialogState {
  name: DriveDialog;
  entries: DriveEntry[];
}

interface State {
  selectedEntries: Set<number>;
  sidebarExpandedKeys: Key[];
  activePage?: RoutesPage;
  sortDescriptor?: any; // Use proper DriveSortDescriptor if available
  activeActionDialog?: DialogState | null;
  activeDigitalSignActionDialog?: DialogState | null;
  entriesBeingDragged: number[];
  contextMenuData: {x: number; y: number; entry?: DriveEntry} | null;
  documents: any;
  walletStatus: 'unknown' | 'connecting' | 'connected' | 'disconnected' | 'reconnecting'; // Add this line
}

interface Actions {
  setSidebarExpandedKeys: (keys: Key[]) => void;
  expandSidebarItem: (key: Key) => void;
  collapseSidebarItem: (key: Key) => void;
  toggleSidebarItem: (key: Key) => void;
  setActivePage: (page: RoutesPage) => void;
  setActiveActionDialog: (
    name: DriveDialog | null,
    entries?: DriveEntry[],
  ) => void;
  setEntriesBeingDragged: (value: number[]) => void;
  selectEntries: (
    entries: (number | undefined | null)[],
    merge?: boolean,
  ) => void;
  deselectEntries: (entries: number[] | 'all') => void;
  reset: () => void;
  setDocuments: (docs: any[]) => void; // Add this line
  setWalletStatus: (status: 'unknown' | 'connecting' | 'connected' | 'disconnected' | 'reconnecting') => void; // Add this line
}

const initialState: State = {
  selectedEntries: new Set(),
  sidebarExpandedKeys: [],
  activeActionDialog: null,
  activeDigitalSignActionDialog: null,
  entriesBeingDragged: [],
  contextMenuData: null,
  documents: { file_entries: [], template_data: [] }, // Add this line
  walletStatus: 'unknown', // Add this line
};

export const useDriveStore = create<State & Actions>()(
  persist(
    immer((set, get) => ({
      ...initialState,
      setSidebarExpandedKeys: keys => {
        set(state => {
          state.sidebarExpandedKeys = keys;
        });
      },
      expandSidebarItem: key => {
        set(state => {
          if (!state.sidebarExpandedKeys.includes(key)) {
            state.sidebarExpandedKeys.push(key);
          }
        });
      },
      collapseSidebarItem: key => {
        set(state => {
          state.sidebarExpandedKeys = state.sidebarExpandedKeys.filter(
            k => k !== key,
          );
        });
      },
      toggleSidebarItem: key => {
        set(state => {
          if (state.sidebarExpandedKeys.includes(key)) {
            state.sidebarExpandedKeys = state.sidebarExpandedKeys.filter(
              k => k !== key,
            );
          } else {
            state.sidebarExpandedKeys.push(key);
          }
        });
      },
      setActivePage: page => {
        set(state => {
          state.activePage = page;
        });
      },
      setActiveActionDialog: (name, entries = stableArray) => {
        set(state => {
          const current = get().activeActionDialog;
          if (current?.name !== name || current.entries !== entries) {
            state.activeActionDialog = name ? {name, entries} : null;
          }
        });
      },
      setEntriesBeingDragged: value => {
        set(state => {
          state.entriesBeingDragged = value;
        });
      },
      selectEntries: (entries, merge = false) => {
        set(state => {
          if (!merge) {
            state.selectedEntries.clear();
          }
          entries.forEach(e => e != null && state.selectedEntries.add(e));
        });
      },
      deselectEntries: entries => {
        set(state => {
          if (!state.selectedEntries.size) return;
          if (entries === 'all') {
            state.selectedEntries.clear();
          } else {
            entries.forEach(e => state.selectedEntries.delete(e));
          }
        });
      },
      reset: () => {
        set(initialState);
      },
      setDocuments: docs => {
        set(state => {
          state.documents = docs;
        });
      },
      setWalletStatus: status => {
        set(state => {
          state.walletStatus = status;
        });
      },
    })),
    {
      name: 'drive-storage',
      partialize: (state) => ({
        selectedEntries: Array.from(state.selectedEntries),
        sidebarExpandedKeys: state.sidebarExpandedKeys,
        activePage: state.activePage,
        sortDescriptor: state.sortDescriptor,
        activeActionDialog: state.activeActionDialog,
        activeDigitalSignActionDialog: state.activeDigitalSignActionDialog,
        entriesBeingDragged: state.entriesBeingDragged,
        contextMenuData: state.contextMenuData,
        documents: state.documents,
        walletStatus: state.walletStatus, // Add this line
      }),
      merge: (
        persistedState: any, // fallback to any to avoid TS error
        currentState: State & Actions
      ) => ({
        ...currentState,
        ...persistedState,
        selectedEntries: new Set(persistedState.selectedEntries || []),
      }),
    }
  )
);

export function driveState() {
  return useDriveStore.getState();
}

export function useActiveDialogEntry() {
  const dialog = useDriveStore(s => s.activeActionDialog);
  return dialog?.entries[0]!;
}
